<script lang="ts">
import '@/types/tower/v2/CustomFabricClasses';

export default {
  name: 'TowerVisualizerCanvas',
};
</script>

<script setup lang="ts">
import { ProgramTower } from '@/types/tower/v2/Tower';
// load the custom fabric objects
import { fabric } from 'fabric';
import { watch, ref, onMounted, inject, markRaw } from 'vue';
import { useStore } from 'vuex';
import { AppState, Commit } from '@/store';
import { StoreMutationType } from '@/types/StoreMutationType';
import { TowerInfo } from '@/views/tower_visualizer/types/TowerVisualizerPage';
import { customEvents } from '@/store/CustomEvents';
import CanvasUtils from '@/utilities/tower/v2/Canvas';

const props = defineProps<{
  width: number;
  height: number;
  tower: ProgramTower;
}>();

let canvas = markRaw({} as fabric.Canvas);

// the current serialized canvas state
const currentCanvas = ref<string>('');

const towerInfo = inject<TowerInfo>('towerInfo');
const store = useStore<AppState>();

onMounted(() => {
  canvas = new fabric.TowerCanvas('c', {
    enableRetinaScaling: false,
    stopContextMenu: true,
    fireRightClick: true,
  });

  if (towerInfo?.canvas) {
    towerInfo.canvas.value = canvas;
  }

  // load the tower
  if (props.tower) {
    loadTower(props.tower);
  }

  // canvas handlers
  canvas.on('object:modified', () => {
    if (!towerInfo?.towerChanged) return;
    towerInfo.towerChanged.value = currentCanvas.value !== JSON.stringify(canvas.toJSON());
  });
  canvas.on('object:added', () => {
    if (!towerInfo?.towerChanged) return;
    towerInfo.towerChanged.value = currentCanvas.value !== JSON.stringify(canvas.toJSON());
  });
  canvas.on('object:removed', () => {
    if (!towerInfo?.towerChanged) return;
    towerInfo.towerChanged.value = currentCanvas.value !== JSON.stringify(canvas.toJSON());
  });
  canvas.on('selection:created', (e: fabric.IEvent) => {
    if (e.selected?.length === 1 && e.selected[0] instanceof fabric.GroupBase && towerInfo) {
      towerInfo.selectedTower.value = e.selected[0].towerCard;
    }
  });
  canvas.on('selection:updated', (e: fabric.IEvent) => {
    if (e.selected?.length === 1 && e.selected[0] instanceof fabric.GroupBase && towerInfo) {
      towerInfo.selectedTower.value = e.selected[0].towerCard;
    }
  });
  canvas.on('selection:cleared', () => {
    if (towerInfo) {
      towerInfo.selectedTower.value = undefined;
    }
  });

  addEventListener(customEvents.towerVisualizerSetCanvasToCurrent, () => {
    currentCanvas.value = JSON.stringify(canvas.toJSON());
    if (towerInfo?.towerChanged) {
      towerInfo.towerChanged.value = false;
    }
  });

  addEventListener('keydown', (e: Event) => {
    const keyEvent = e as KeyboardEvent;
    if (import.meta.env.MODE === 'development' && keyEvent.key === ' ') {
      console.log('handleKeydown: current canvas');
      window.canvas = canvas;
    }
  });

  store?.commit(StoreMutationType.SHOW_VISUALIZER_EXPORT_OPTIONS, false);
});

// ----------
// watchers
// ----------
watch(
  () => props.width,
  (width) => {
    canvas.setWidth(width);
  },
);

watch(
  () => props.height,
  (height) => {
    canvas.setHeight(height);
  },
);

watch(
  () => store.state.towerCanvasState.zoomLevel,
  (zoomLevel) => {
    handleZoom(zoomLevel);
  },
);

// ----------
// functions
// ----------
function handleZoom(zoomLevel?: number): void {
  if (!canvas || !zoomLevel) return;

  const center = canvas.getCenter() ?? { left: 0, top: 0 };
  const centerPoint = new fabric.Point(center.left, center.top);

  canvas.zoomToPoint(centerPoint, zoomLevel);
  canvas.getObjects().forEach((obj) => obj.setCoords());
  canvas.requestRenderAll();
}

function loadTower(tower: ProgramTower): void {
  try {
    const resizeWidth = canvas.getWidth() * 0.9;
    const resizeHeight = canvas.getHeight() * 0.9;
    

    canvas.loadFromJSON(tower.towerObject, () => {
      canvas.getObjects().forEach((obj) => {
        obj.set({
          includeDefaultValues: false,
        });
        
        const objWidth = obj.width ?? 1;
        const objHeight = obj.height ?? 1;

        if (objWidth > resizeWidth || objHeight > resizeHeight) {
          const scaleWidth = resizeWidth / objWidth;
          const scaleHeight = resizeHeight / objHeight;
          const scale = Math.min(scaleWidth, scaleHeight);

          obj.scale(scale);
        }
    
        canvas.centerObject(obj);
      });
      canvas.renderAll();

      currentCanvas.value = JSON.stringify(canvas.toJSON());
      if (towerInfo) {
        towerInfo.towerChanged.value = false;
      }
      Commit(StoreMutationType.SET_TOWER_CANVAS_ZOOM_LEVEL, canvas.getZoom() ?? CanvasUtils.DEFAULT_ZOOM);
    });
  } catch (e) {
    console.error(e);
  }
}
</script>

<template>
  <canvas id="c" class="canvas" :style="{ width: `${width}px`, height: `${height}px` }" :width="width" :height="height">
  </canvas>
</template>

<style>
.canvas {
  z-index: var(--zindex-canvas);
}
</style>
